import {
    readRawKernelBucket,
    saveKernelCloudFirst,
} from "./kernelStore.js";
import {
    readRawCanvasTemplateBucket,
    saveCanvasTemplateCloudFirst,
} from "./canvasTemplateStore.js";
import { openTenantViewWriteJournal } from "./tenantViewWriteJournal.js";

function records(result, bucketId, report) {
    if (result?.status === "ok") return result.records;
    if (result?.status === "absent") return [];
    report.push({
        bucketId,
        localId: null,
        status: "failed",
        error: result?.error instanceof Error
            ? result.error
            : new Error(`local tenant-view bucket is ${result?.status || "invalid"}`),
    });
    return [];
}

function pendingJournal(tenantId, kind, record, storage) {
    if (typeof record?.id !== "string" || !record.id) return null;
    const journal = openTenantViewWriteJournal({
        tenantId,
        operation: "create",
        kind,
        sourceKey: record.id,
    }, { storage });
    return journal.read() ? journal : null;
}

/**
 * Runs before the legacy importer. It resumes only rows already branded by a
 * durable cloud-create journal, kernels first, so the importer cannot send the
 * same staging row through its distinct Import idempotency domain.
 */
export async function recoverPendingTenantViewCreates({
    tenantId,
    storage = globalThis.localStorage,
    bucketIds = ["default", tenantId],
    readKernelBucket = readRawKernelBucket,
    readCanvasBucket = readRawCanvasTemplateBucket,
    saveKernel = saveKernelCloudFirst,
    saveCanvas = saveCanvasTemplateCloudFirst,
} = {}) {
    if (!tenantId) throw new TypeError("tenantId is required for pending create recovery");
    const uniqueBuckets = [...new Set(bucketIds.filter(Boolean))];
    const report = { kernels: [], canvases: [] };

    for (const bucketId of uniqueBuckets) {
        for (const record of records(
            readKernelBucket(bucketId),
            bucketId,
            report.kernels,
        )) {
            try {
                if (!pendingJournal(tenantId, "kernel", record, storage)) continue;
            } catch (error) {
                // Do not retry malformed durable state through a fresh
                // request-id domain, but keep scanning independent records.
                report.kernels.push({
                    bucketId,
                    localId: record?.id ?? null,
                    status: "failed",
                    error,
                });
                continue;
            }
            try {
                const result = await saveKernel(bucketId, record, {
                    cloudTenantId: tenantId,
                });
                report.kernels.push({
                    bucketId,
                    localId: record.id,
                    status: result?.tenantViewId ? "recovered" : "pending",
                });
            } catch (error) {
                report.kernels.push({
                    bucketId,
                    localId: record.id,
                    status: "failed",
                    error,
                });
            }
        }
    }

    for (const bucketId of uniqueBuckets) {
        for (const record of records(
            readCanvasBucket(bucketId),
            bucketId,
            report.canvases,
        )) {
            try {
                if (!pendingJournal(tenantId, "canvas", record, storage)) continue;
            } catch (error) {
                report.canvases.push({
                    bucketId,
                    localId: record?.id ?? null,
                    status: "failed",
                    error,
                });
                continue;
            }
            try {
                const result = await saveCanvas(bucketId, record, {
                    cloudTenantId: tenantId,
                });
                report.canvases.push({
                    bucketId,
                    localId: record.id,
                    status: result?.tenantViewId ? "recovered" : "pending",
                });
            } catch (error) {
                report.canvases.push({
                    bucketId,
                    localId: record.id,
                    status: "failed",
                    error,
                });
            }
        }
    }

    return report;
}
